import { useEffect, useState, useContext } from "react";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import {
  createSearchParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import {
  calculateDurationByWeightForCopy,
  getQueryParams,
  useCurrentLocation,
} from "lib/utils";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { useTranslation } from "react-i18next";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import AuthContext from "contexts/AuthContext";
import WeightEstimation from "components/weight-estimation";
import { localizeDecimalSeparator } from "lib/utils";
import { calculateEstimate } from "lib/calculateEstimate";
import { Scheduler } from "components/appointments/scheduler";
import { Input } from "components/input";
import { Label } from "components/label";
import stores from "consts/stores.json";
import AppointmentErrorModal from "components/appointment-error-modal";
import { AnimatePresence } from "framer-motion";
import { EVENT_16 } from "utils/tracking";

const { REACT_APP_APPOINTMENT_MAX_DATE } = process.env;

export default function AppointmentPage() {
  const { t } = useTranslation();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const { location: currentLocation } = useCurrentLocation();
  const { setSidebarVisual } = useOutletContext();
  const { user } = useContext(AuthContext);
  const [userInfo, setUserInfo] = useState({
    email: user?.email || "",
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
  });
  const [errors, setErrors] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    setSidebarVisual(currentLocation.locale === "de-DE" ? "euro" : "dollar");
  }, [setSidebarVisual, currentLocation]);

  // Get query params and restart flow if absent
  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  useRestartIfAbsent(
    queryParams,
    ["w", TAKEBACK_METHOD_NAME, "p"],
    t("urls.trade_in_weight")
  );

  const methodName =
    queryParams.p === "giftcard"
      ? t("trade-in.summary.giftcardTitle")
      : t("trade-in.summary.cashTitle");
  const estimate = calculateEstimate(queryParams.w);

  const validateForm = () => {
    const newErrors = {};
    if (!userInfo.email) newErrors.email = t("validation.email_required");
    if (!userInfo.firstName)
      newErrors.firstName = t("validation.first_name_required");
    if (!userInfo.lastName)
      newErrors.lastName = t("validation.last_name_required");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (userInfo.email && !emailRegex.test(userInfo.email)) {
      newErrors.email = t("validation.email_invalid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canSubmit = () => {
    if (!selectedAppointment) return false;
    if (!user) {
      return userInfo.email && userInfo.firstName && userInfo.lastName;
    }
    return true;
  };

  return (
    <FormContainer
      submitLabel={t("ui.button_continue")}
      submitHref={t("urls.trade_in_summary")}
      submitParams={queryParams}
      submitDisabled={!canSubmit()}
      onSubmit={() => {
        if (!user && !validateForm()) {
          return false;
        }
        return true;
      }}
      secondaryLabel={t("ui.button_back")}
      secondaryParams={queryParams}
      secondaryHref={t("urls.trade_in_checklist")}
      submitTracking={[EVENT_16]}
    >
      <ProgressBar step={5} />
      <div className="flex flex-col gap-regular">
        <h2 className="font-semibold text-6xl max-w-lg">
          {t("trade-in.appointment.title")}
        </h2>
        <p className="text-[#7E7D7D] text-2xl leading-normal font-light">
          {t("trade-in.appointment.description", {
            weight: queryParams.w.toFixed(2),
            unit: "lbs",
            store: stores.find(
              ({ value }) =>
                value ===
                currentLocation?.takebackMethod?.find((method) =>
                  method.startsWith("in_store")
                )
            )?.name,
            duration: calculateDurationByWeightForCopy(queryParams.w),
          })}
        </p>
        {!user && (
          <div className="space-y-4 mt-6">
            <div>
              <Label htmlFor="email" required>
                {t("ui.label_email")}
              </Label>
              <Input
                id="email"
                type="email"
                value={userInfo.email}
                onChange={(e) =>
                  setUserInfo({ ...userInfo, email: e.target.value })
                }
                error={errors.email}
                placeholder={t("ui.placeholder_email")}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" required>
                  {t("ui.label_first_name")}
                </Label>
                <Input
                  id="firstName"
                  value={userInfo.firstName}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, firstName: e.target.value })
                  }
                  error={errors.firstName}
                  placeholder={t("ui.placeholder_first_name")}
                />
              </div>
              <div>
                <Label htmlFor="lastName" required>
                  {t("ui.label_last_name")}
                </Label>
                <Input
                  id="lastName"
                  value={userInfo.lastName}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, lastName: e.target.value })
                  }
                  error={errors.lastName}
                  placeholder={t("ui.placeholder_last_name")}
                />
              </div>
            </div>
          </div>
        )}
        <div className="">
          <Scheduler
            user={user}
            email={userInfo.email}
            firstName={userInfo.firstName}
            lastName={userInfo.lastName}
            currentTime={selectedAppointment}
            weight={queryParams.w}
            state={queryParams[TAKEBACK_METHOD_NAME]}
            maxDate={REACT_APP_APPOINTMENT_MAX_DATE}
            onTimeSelect={(slot) => {
              if (!user && !validateForm()) {
                return;
              }
              createSearchParams({
                ...queryParams,
                appointmentId: slot.appointment.id,
              });
              setSelectedAppointment(slot.time);
              localStorage.setItem("appointment", JSON.stringify(slot));
            }}
            onBookingError={() => {
              setShowErrorModal(true);
            }}
          />
          <WeightEstimation
            className="mt-4 hidden md:flex"
            visual={queryParams.p === "giftcard" ? "giftcard" : "cash"}
            title={t("ui.estimate_part1")}
            locale={currentLocation.locale}
            singleValue={
              true
                ? queryParams.p === "giftcard"
                  ? localizeDecimalSeparator(estimate.gift, currentLocation)
                  : localizeDecimalSeparator(estimate.payment, currentLocation)
                : localizeDecimalSeparator(estimate.gift, currentLocation)
            }
            animate
            description={`${methodName}. ${t("ui.estimate_part2", {
              weight: queryParams.w.toFixed(2),
              unit: "lbs",
            })}`}
          />
        </div>
      </div>
      <AnimatePresence>
        {showErrorModal && (
          <AppointmentErrorModal
            modalOpen={showErrorModal}
            setModalOpen={setShowErrorModal}
          />
        )}
      </AnimatePresence>
    </FormContainer>
  );
}

// ? setSelectedAppointment(`${date}-${slot.time}`)
